// Quote Builder — reactive state + PG sync.
//
// V1 model:
//   • LocalStorage `kos_quote_builder_v3` is a write-through cache so
//     the page is interactive offline / before first server response.
//   • Mutations debounce a server save at ~800 ms (POST on first save,
//     PUT after that — keyed by `serverId`).
//   • On mount, we try to hydrate the quote from PG. If the server has
//     a row whose `client_id` matches our local id, we adopt the
//     server's shape (canonical reference, `serverId`, server totals).
//     If neither side has anything, start a fresh default quote.
//
// Offline-first proper (IndexedDB + sync queue, calendar-style) is
// deferred until V2 — the Quote Builder is one big document not a list
// of records, so the existing `createRepo` helper isn't a clean fit.
// The LS cache + debounced fetch model is enough to deliver the
// "doesn't lose work" + "syncs across devices" promise for V1.

import { api, ApiError } from "@tkos/api-client";
import type { QuoteBuilderState, QuoteDefaults, QuoteZone } from "./types";
import { DEFAULT_BOTTOM_MM, DEFAULT_TOP_MM } from "./types";
import { newId, tempQuoteRef } from "./formatters";
import { quoteTotals } from "./pricing";

const STORE_KEY = "kos_quote_builder_v3";
/** localStorage flag used by the dashboard's resume-banner. */
export const MINIMISED_FLAG = "kos_qb_v3_minimised";

// Retailer VAT-registration, seeded from settings on QB mount. It controls
// only the DEFAULT VAT rate of a brand-new quote — the per-quote VAT field
// stays editable. `null` = not yet known → preserve historical behaviour (VAT
// applied at 20%, which is what every existing tenant has had). `false` = the
// retailer is not VAT registered → new quotes start ex-VAT (rate 0).
let seededVatRegistered: boolean | null = null;
/** Called once the retailer profile has loaded so subsequent new quotes
 *  (Clear / fresh open) default to the correct VAT rate. */
export function setRetailerVatRegistered(v: boolean): void {
  seededVatRegistered = v;
}
/** The VAT rate a brand-new quote should start with, per the setting above. */
export function defaultVatRate(): number {
  return seededVatRegistered === false ? 0 : 20;
}

// Retailer's default intro/closing notes, seeded from settings on QB mount so
// a brand-new quote starts with the retailer's standard cover/closing copy
// (editable per quote). Empty until the profile loads.
let seededIntroNote = "";
let seededClosingNote = "";
export function setRetailerQuoteNoteDefaults(intro: string, closing: string): void {
  seededIntroNote = intro || "";
  seededClosingNote = closing || "";
}
export function defaultIntroNote(): string { return seededIntroNote; }
export function defaultClosingNote(): string { return seededClosingNote; }

// ── Defaults ────────────────────────────────────────────────────────────────

function defaultZone(name = "Kitchen"): QuoteZone {
  return { id: newId("z"), name, note: "", summary: "", items: [] };
}

/** Verbatim port of source `defaultDefaults()` at kos-qb-v3.js:201–216. */
export function defaultDefaults(): QuoteDefaults {
  return {
    door:      { supplier: "TKC", range: "", finish: "", drilling: { hingeCount: 0, topMm: DEFAULT_TOP_MM, bottomMm: DEFAULT_BOTTOM_MM, side: "L" }, integratedHandle: "none", doorType: "stock", doorUnitType: "base", drillMode: "none" },
    cabinet:   { supplier: "TKC", construction: "standard", carcassFinish: "White", carcassFinishCode: "", carcassPriceGroup: 1, edging: "Matching carcass", edgingCode: "", hinging: "L" },
    handle:    { supplier: "TKC" },
    storage:   { supplier: "TKC", subType: "Bins" },
    worktop:   { supplier: "TKC", material: "Laminate" },
    panel:     { supplier: "TKC", type: "Base End Panel" },
    plinth:    { supplier: "TKC", type: "Plinth" },
    lighting:  { supplier: "TKC", type: "LED Strip" },
    appliance: { supplier: "TKC", type: "Oven" },
    sundries:  { supplier: "TKC", type: "Brackets" },
    labour:    { rate: 0 },
    remember:  { edging: true, drilling: true, carcassFinish: true, range: true, finish: true, construction: true },
    // Quote-level default markup % seeded into every new line item.
    // Surfaced as an input in Quote Details; persisted via defaults JSONB.
    // Default 0 — retailer sets their own margin consciously per quote;
    // no implicit 40% assumption (Steve 2026-05-15).
    markupPct: 0,
    // SJB cabinet catalogue: set once per quote, inherited by each new
    // cabinet line, editable per line thereafter. Blank boardKey means the
    // retailer has not picked a colour yet — the picker asks before it will
    // add a line, rather than guessing one and mis-pricing.
    sjb: { boardKey: "", boardLabel: "", hingeFinish: "standard", drawerSystem: "antaro",
           edgingColour: "Matching carcass",
           // Doors on. Matches every quote built before the toggle existed,
           // and a cabinet that silently arrives with no front is a worse
           // failure than one the retailer has to switch off.
           frontalOn: true,
           // Blank: which door catalogues exist is server config, so a new
           // quote must not assume one. The picker preselects when exactly
           // one comes back, and remembers whatever is chosen from then on.
           frontalSlug: "",
           frontalRange: "", frontalColour: "", frontalTier: "",
           rememberFrontal: true },
  };
}

/** Deep-merge incoming defaults onto the canonical shape so payloads from
 *  older clients (no `defaults` field) and partial updates both yield a
 *  fully-typed `QuoteDefaults`. */
function mergeDefaults(raw: unknown): QuoteDefaults {
  const base = defaultDefaults();
  if (!raw || typeof raw !== "object") return base;
  const src = raw as Record<string, unknown>;
  const out = base as unknown as Record<string, unknown>;
  for (const key of Object.keys(base) as Array<keyof QuoteDefaults>) {
    const srcVal = src[key];
    const baseVal = out[key];
    if (srcVal && typeof srcVal === "object" && !Array.isArray(srcVal) &&
        baseVal && typeof baseVal === "object" && !Array.isArray(baseVal)) {
      // Door has a nested `drilling` object — one level of deep merge is
      // enough for the current shape; do it inline.
      const merged: Record<string, unknown> = { ...(baseVal as Record<string, unknown>) };
      for (const sk of Object.keys(srcVal)) {
        const sv = (srcVal as Record<string, unknown>)[sk];
        const bv = merged[sk];
        if (sv && typeof sv === "object" && !Array.isArray(sv) &&
            bv && typeof bv === "object" && !Array.isArray(bv)) {
          merged[sk] = { ...(bv as Record<string, unknown>), ...(sv as Record<string, unknown>) };
        } else if (sv !== undefined) {
          merged[sk] = sv;
        }
      }
      out[key] = merged;
    } else if (srcVal !== undefined && srcVal !== null && typeof srcVal !== "object") {
      // Scalar override (e.g. markupPct: 35 from a persisted quote).
      // Without this branch, scalar fields in `defaults` would never
      // round-trip from LS/server — the outer loop only handled object
      // children. Falls back to the base value if srcVal is missing.
      out[key] = srcVal;
    }
  }
  return out as unknown as QuoteDefaults;
}

export function defaultQuote(): QuoteBuilderState {
  const today = new Date().toISOString().slice(0, 10);
  return {
    version: 3,
    id: newId("q"),
    reference: tempQuoteRef(),
    customer: "",
    project: "",
    date: today,
    validDays: 30,
    vatRate: defaultVatRate(),
    status: "draft",
    notes: "",
    introNote: defaultIntroNote(),
    closingNote: defaultClosingNote(),
    attachedDesigns: [],
    groupBy: "zone",
    zones: [defaultZone()],
    defaults: defaultDefaults(),
    supplierDiscounts: {},
    zoneSummary: {},
    deliveryChargePence: 0,
    lastSavedAt: Date.now(),
    createdAt: Date.now(),
  };
}

// ── Shape-guard (forward-compat with legacy payloads) ───────────────────────

function ensureShape(raw: unknown): QuoteBuilderState {
  const q = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const base = defaultQuote();
  // Quote-level markup default to use when a legacy line item is missing
  // its own markupPct. Read from the raw defaults blob before mergeDefaults
  // canonicalises it. Final fallback is 40 (the historical literal).
  const rawDefaults = (q.defaults && typeof q.defaults === "object" ? q.defaults : {}) as Record<string, unknown>;
  const quoteMarkupDefault = typeof rawDefaults.markupPct === "number" ? rawDefaults.markupPct : 0;
  const zones = Array.isArray(q.zones) && q.zones.length > 0
    ? (q.zones as unknown[]).map((z) => {
        const zo = (z && typeof z === "object" ? z : {}) as Record<string, unknown>;
        return {
          id: typeof zo.id === "string" ? zo.id : newId("z"),
          name: typeof zo.name === "string" ? zo.name : "Kitchen",
          note: typeof zo.note === "string" ? zo.note : "",
          summary: typeof zo.summary === "string" ? zo.summary : "",
          items: Array.isArray(zo.items) ? (zo.items as QuoteZone["items"]).map((it) => ({
            ...it,
            id: it.id ?? newId("i"),
            markupMode: it.markupMode ?? "pct",
            // Per-line fallback now defers to the quote's own default before
            // the historical 40. New lines added under a non-40 quote default
            // already carry that value (each add path reads store.get()
            // .defaults.markupPct); this branch only matters for legacy
            // lines that pre-date the field.
            markupPct: it.markupPct ?? quoteMarkupDefault,
            markupGbp: it.markupGbp ?? 0,
            qty: it.qty ?? 1,
            notes: it.notes ?? "",
            category: it.category ?? "cabinets",
            supplier: it.supplier ?? "Manual",
          })) : [],
        };
      })
    : base.zones;

  return {
    ...base,
    ...(q as Partial<QuoteBuilderState>),
    version: 3,
    zones,
    defaults: mergeDefaults(q.defaults),
    supplierDiscounts: (q.supplierDiscounts as Record<string, number>) ?? {},
    zoneSummary: (q.zoneSummary as Record<string, boolean>) ?? {},
    attachedDesigns: Array.isArray(q.attachedDesigns) ? (q.attachedDesigns as string[]) : [],
    deliveryChargePence: typeof q.deliveryChargePence === "number" ? q.deliveryChargePence : 0,
    vatRate: typeof q.vatRate === "number" ? q.vatRate : 20,
    validDays: typeof q.validDays === "number" ? q.validDays : 30,
    status: (q.status as QuoteBuilderState["status"]) ?? "draft",
    groupBy: (q.groupBy as QuoteBuilderState["groupBy"]) ?? "zone",
    reference: typeof q.reference === "string" ? q.reference : base.reference,
  };
}

function readFromStorage(): QuoteBuilderState | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    return ensureShape(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeToStorage(state: QuoteBuilderState): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(state));
  } catch {
    /* quota exceeded — swallow */
  }
}

// ── Wire shape helpers (client ↔ server) ───────────────────────────────────

/** Client state → POST/PUT payload for /api/portal/quote-builder.
 *
 *  `extra` carries the two last-write-wins fields. They are NOT part of the
 *  quote's state: `baseVersion` is the row version this copy was loaded at, and
 *  `allowItemClear` is a one-shot statement of user intent held in the store
 *  closure. Passing them in rather than reading them off state keeps them out of
 *  the localStorage snapshot — which matters, because a persisted
 *  `allowItemClear` would let a reopened stale tab authorise its own wipe. */
export function toWire(
  s: QuoteBuilderState,
  extra: { baseVersion?: number; allowItemClear?: boolean } = {},
): Record<string, unknown> {
  const t = quoteTotals(s);
  return {
    ...(typeof extra.baseVersion === "number" ? { baseVersion: extra.baseVersion } : {}),
    ...(extra.allowItemClear ? { allowItemClear: true } : {}),
    clientId: s.id,
    reference: s.reference,
    customer: s.customer,
    // CRM linkage — populated by the customer typeahead or the QB auto-CRM
    // checkbox. The server stores this on quotes.customer_id when the value
    // is a uuid (CRM contacts created via PG insert), which is what powers
    // the "Quotes" card on the CRM customer record page.
    customerId: s.customerId ?? "",
    customerEmail: s.customerEmail ?? "",
    customerPhone: s.customerPhone ?? "",
    project: s.project,
    status: s.status,
    groupBy: s.groupBy,
    vatRate: s.vatRate,
    validDays: s.validDays,
    notes: s.notes,
    termsAndConditions: s.termsAndConditions ?? "",
    introNote: s.introNote ?? "",
    closingNote: s.closingNote ?? "",
    // TODO(server): `customerSignature` not yet in the portal_quotes schema —
    // round-trip is lossy across PG sync. Persists in LS until schema lands.
    ...(s.customerSignature !== undefined && { customerSignature: s.customerSignature }),
    deliveryChargePence: s.deliveryChargePence,
    attachedDesigns: s.attachedDesigns,
    supplierDiscounts: s.supplierDiscounts,
    zoneSummary: s.zoneSummary,
    defaults: s.defaults,
    zones: s.zones.map((z) => ({
      name: z.name,
      note: z.note,
      summary: z.summary,
      items: z.items.map((it) => ({
        desc: it.desc,
        code: it.code,
        category: it.category,
        supplier: it.supplier,
        qty: it.qty,
        listPrice: it.listPrice,
        discPct: it.discPct,
        tradePrice: it.tradePrice,
        markupMode: it.markupMode,
        markupPct: it.markupPct,
        markupGbp: it.markupGbp,
        retailPrice: it.retailPrice,
        notes: it.notes,
        width: it.width,
        height: it.height,
        depth: it.depth,
        metadata: {
          ...(it.doorRange !== undefined && { doorRange: it.doorRange }),
          ...(it.doorColour !== undefined && { doorColour: it.doorColour }),
          ...(it.carcassFinish !== undefined && { carcassFinish: it.carcassFinish }),
          ...(it.edging !== undefined && { edging: it.edging }),
          ...(it.hinging !== undefined && { hinging: it.hinging }),
          ...(it.material !== undefined && { material: it.material }),
          ...(it.length !== undefined && { length: it.length }),
          ...(it.thickness !== undefined && { thickness: it.thickness }),
          ...(it.unitTypeId !== undefined && { unitTypeId: it.unitTypeId }),
          ...(it.unitTypeLabel !== undefined && { unitTypeLabel: it.unitTypeLabel }),
          // ── Correct door picker fields (port of kos-qb-v3.js renderDoorConfigurator) ──
          ...(it.doorType !== undefined && { doorType: it.doorType }),
          ...(it.doorBespokeText !== undefined && { doorBespokeText: it.doorBespokeText }),
          ...(it.doorUnitType !== undefined && { doorUnitType: it.doorUnitType }),
          ...(it.doorHoleQty !== undefined && { doorHoleQty: it.doorHoleQty }),
          ...(it.doorHoleMidMm !== undefined && { doorHoleMidMm: it.doorHoleMidMm }),
          ...(it.handing !== undefined && { handing: it.handing }),
          ...(it.drillingPositions !== undefined && { drillingPositions: it.drillingPositions }),
          ...(it.drillingFrom !== undefined && { drillingFrom: it.drillingFrom }),
          ...(it.topMm !== undefined && { topMm: it.topMm }),
          ...(it.bottomMm !== undefined && { bottomMm: it.bottomMm }),
        },
      })),
    })),
    // Totals — persisted so PG is the source of truth for reporting.
    // These were hardcoded to 0 (with a comment claiming the breakdown is
    // "recomputed client-side"), which meant EVERY quotes row was written with
    // subtotal/vat/total/trade/profit = 0.00 while quote_items carried the real
    // money. That silently zeroed Revenue Overview, /reports, Kit AI's pipeline
    // figures, quote_analytics and the CRM value column. vatRate and
    // deliveryChargePence were always sent, which is why those two columns were
    // the only populated ones. Commission/GMV never read these — they derive
    // from the Stripe PI amount — so no billing was affected.
    // Integer pence throughout; the server divides by 100 on write.
    subtotalPence:   t.retailSubPence,
    // ALL the VAT, delivery's included — quotes.vat_amount sits beside
    // delivery_charge and total_inc_vat, so subtotal + delivery_charge +
    // vat_amount = total_inc_vat holds on the row (migration 0093 brought the
    // old rows into line). Only the WIRE value is combined: quoteTotals()
    // keeps vatAmountPence items-only because pricing.ts, totals.ts and pdf.ts
    // all add deliveryVatPence themselves.
    vatAmountPence:  t.vatAmountPence + t.deliveryVatPence,
    grandTotalPence: t.grandTotalPence,
    tradeCostPence:  t.tradeCostPence,
    profitPence:     t.profitPence,
    marginPct:       t.marginPct,
  };
}

interface ServerQuote {
  id: string;
  clientId: string | null;
  /** `quotes.version` — bumped by the server on every write. Round-tripped as
   *  `baseVersion` so a stale save is refused rather than silently winning. */
  version?: number;
  reference: string;
  customer: string;
  customerId?: string;
  customerEmail: string;
  customerPhone: string;
  project: string;
  status: QuoteBuilderState["status"];
  groupBy: QuoteBuilderState["groupBy"];
  vatRate: number;
  validDays: number;
  notes: string;
  termsAndConditions: string;
  introNote?: string;
  closingNote?: string;
  deliveryChargePence: number;
  attachedDesigns: string[];
  supplierDiscounts: Record<string, number>;
  zoneSummary: Record<string, boolean>;
  defaults?: unknown;
  zones: Array<{
    id: string;
    name: string;
    note: string;
    summary?: string;
    items: Array<{
      id: string;
      desc: string;
      code: string;
      category: string;
      supplier: string;
      qty: number;
      listPrice: number;
      discPct: number;
      tradePrice: number;
      markupMode: string;
      markupPct: number;
      markupGbp: number;
      retailPrice: number;
      notes: string;
      width: number;
      height: number;
      depth: number;
      metadata: Record<string, unknown>;
    }>;
  }>;
  openedAt?: number | null;
  createdAt: number;
  updatedAt: number;
}

/** Server quote → client state. Preserves the existing client `id`
 *  (we keep the local id so subscribers don't see it flip on first
 *  sync); the server's UUID is stashed in `serverId`. */
function fromWire(server: ServerQuote, keepClientId: string): QuoteBuilderState {
  // Quote-level markup default from the server's defaults JSONB; falls back
  // to the historical 40 for pre-feature quotes. Threaded into the per-line
  // fallback below so legacy lines inherit the quote's choice rather than
  // a hardcoded 40.
  const serverDefaults = (server.defaults && typeof server.defaults === "object" ? server.defaults : {}) as Record<string, unknown>;
  const quoteMarkupDefault = typeof serverDefaults.markupPct === "number" ? serverDefaults.markupPct : 0;
  return ensureShape({
    id: keepClientId,
    serverId: server.id,
    // Adopt the server's row version so the next save can prove it is not
    // stale. Defaults to 1 for rows written before versioning was bumped.
    serverVersion: server.version ?? 1,
    reference: server.reference || tempQuoteRef(),
    customer: server.customer || "",
    // CRM linkage — the server stores/returns this on quotes.customer_id.
    // Without it the plans/designs gallery (scoped by customerId) stays
    // empty when a saved quote is opened from the customer record, until a
    // manual reassign. Map it through so designs load on mount.
    customerId: server.customerId || "",
    customerEmail: server.customerEmail || "",
    customerPhone: server.customerPhone || "",
    project: server.project || "",
    status: server.status || "draft",
    groupBy: server.groupBy || "zone",
    vatRate: server.vatRate ?? 20,
    validDays: server.validDays ?? 30,
    notes: server.notes || "",
    termsAndConditions: server.termsAndConditions || "",
    introNote: server.introNote || "",
    closingNote: server.closingNote || "",
    deliveryChargePence: server.deliveryChargePence ?? 0,
    attachedDesigns: server.attachedDesigns ?? [],
    supplierDiscounts: server.supplierDiscounts ?? {},
    zoneSummary: server.zoneSummary ?? {},
    defaults: mergeDefaults(server.defaults),
    zones: (server.zones ?? []).map((z) => ({
      summary: z.summary || "",
      id: z.id || newId("z"),
      name: z.name || "Kitchen",
      note: z.note || "",
      items: (z.items ?? []).map((it) => ({
        id: it.id || newId("i"),
        desc: it.desc || "",
        code: it.code || "",
        category: (it.category as QuoteBuilderState["zones"][number]["items"][number]["category"]) || "cabinets",
        supplier: it.supplier || "Manual",
        qty: it.qty || 0,
        listPrice: it.listPrice || 0,
        discPct: it.discPct || 0,
        tradePrice: it.tradePrice || 0,
        markupMode: (it.markupMode === "gbp" ? "gbp" : "pct"),
        markupPct: it.markupPct || quoteMarkupDefault,
        markupGbp: it.markupGbp || 0,
        retailPrice: it.retailPrice || 0,
        notes: it.notes || "",
        width: it.width || 0,
        height: it.height || 0,
        depth: it.depth || 0,
        ...(it.metadata ?? {}),
      })),
    })),
    date: new Date().toISOString().slice(0, 10),
    lastSavedAt: server.updatedAt || Date.now(),
    lastServerSyncAt: server.updatedAt || Date.now(),
    openedAt: server.openedAt || 0,
    createdAt: server.createdAt || Date.now(),
  });
}

// ── Server sync ─────────────────────────────────────────────────────────────

interface SaveResult {
  ok: boolean;
  serverId?: string;
  reference?: string;
  /** The row version the server reports AFTER this write, adopted so the next
   *  save is not itself treated as stale. */
  version?: number;
  /** Set when the server REFUSED the write via the last-write-wins guard.
   *  `stale_write` = someone else changed the quote since we loaded it;
   *  `refusing_item_wipe` / `refusing_zone_wipe` = the payload would have
   *  emptied a populated quote without explicit intent. Distinguished from a
   *  generic failure because the user needs a real decision, not a retry. */
  blocked?: "stale_write" | "refusing_item_wipe" | "refusing_zone_wipe";
  /** Server's current row version, when it told us (409 responses include it). */
  storedVersion?: number;
  message?: string;
}

/** Pull the guard's error code out of an ApiError. `api()` puts the raw response
 *  body in `.message`, so a 409 arrives as a JSON string. */
function readBlock(err: unknown): Pick<SaveResult, "blocked" | "storedVersion" | "message"> | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  try {
    const b = JSON.parse(err.message) as {
      error?: string; storedVersion?: number; message?: string;
    };
    if (b.error === "stale_write" || b.error === "refusing_item_wipe" || b.error === "refusing_zone_wipe") {
      const out: Pick<SaveResult, "blocked" | "storedVersion" | "message"> = { blocked: b.error };
      if (typeof b.storedVersion === "number") out.storedVersion = b.storedVersion;
      if (b.message) out.message = b.message;
      return out;
    }
  } catch { /* not JSON — fall through to a generic failure */ }
  return null;
}

async function fetchByClientId(clientId: string): Promise<ServerQuote | null> {
  try {
    const res = await api<{ ok: boolean; quotes: ServerQuote[] }>(`/portal/quote-builder`);
    return (res.quotes ?? []).find((q) => q.clientId === clientId) ?? null;
  } catch (err) {
    console.warn("[qb-state] fetchByClientId failed", err);
    return null;
  }
}

async function fetchById(serverId: string): Promise<ServerQuote | null> {
  try {
    const res = await api<{ ok: boolean; quote: ServerQuote }>(
      `/portal/quote-builder/${encodeURIComponent(serverId)}`,
    );
    return res.quote ?? null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    console.warn("[qb-state] fetchById failed", err);
    return null;
  }
}

async function postNew(
  state: QuoteBuilderState,
  extra: { baseVersion?: number; allowItemClear?: boolean },
): Promise<SaveResult> {
  try {
    const res = await api<{ ok: boolean; quote: ServerQuote }>(`/portal/quote-builder`, {
      method: "POST",
      body: JSON.stringify(toWire(state, extra)),
      headers: { "content-type": "application/json" },
    });
    if (res.quote) {
      const result: SaveResult = {
        ok: true,
        serverId: res.quote.id,
      };
      if (res.quote.reference) result.reference = res.quote.reference;
      if (typeof res.quote.version === "number") result.version = res.quote.version;
      return result;
    }
    return { ok: false };
  } catch (err) {
    // A POST can hit the guard too: the self-heal branch updates an existing
    // row, so a client that lost its serverId can still arrive with a stale or
    // emptied payload.
    const blocked = readBlock(err);
    if (blocked) return { ok: false, ...blocked };
    console.warn("[qb-state] postNew failed", err);
    return { ok: false };
  }
}

async function putExisting(
  state: QuoteBuilderState,
  extra: { baseVersion?: number; allowItemClear?: boolean },
): Promise<SaveResult> {
  if (!state.serverId) return { ok: false };
  try {
    const res = await api<{ ok: boolean; quote?: ServerQuote }>(
      `/portal/quote-builder/${encodeURIComponent(state.serverId)}`,
      {
        method: "PUT",
        body: JSON.stringify(toWire(state, extra)),
        headers: { "content-type": "application/json" },
      },
    );
    const out: SaveResult = { ok: true, serverId: state.serverId };
    if (typeof res.quote?.version === "number") out.version = res.quote.version;
    return out;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      // Row vanished server-side; clear the serverId so next save POSTs.
      return { ok: false };
    }
    const blocked = readBlock(err);
    if (blocked) return { ok: false, ...blocked };
    console.warn("[qb-state] putExisting failed", err);
    return { ok: false };
  }
}

// ── Store factory ────────────────────────────────────────────────────────────

type Listener = (state: QuoteBuilderState) => void;

export interface QuoteStore {
  get(): QuoteBuilderState;
  set(mutator: (draft: QuoteBuilderState) => void): void;
  /** Replace the entire state — used by Clear / Load from archive. */
  replace(state: QuoteBuilderState): void;
  /** Drop the local working copy — blank in memory, snapshot removed, pending
   *  saves cancelled — so the Quote Builder starts clean next time.
   *
   *  Used by Close, Delete, and the fresh=1 entry point. Deliberately NOT
   *  `replace(defaultQuote())`: replace() schedules a server sync, and a blank
   *  quote with no serverId POSTs as a brand-new empty row, so every Close
   *  would litter PG with empty drafts. This touches no server state at all.
   *  The quote itself is already saved and stays reachable from Load quote. */
  resetLocal(): void;
  /** Seed the default VAT rate onto an as-yet-untouched quote (from the
   *  retailer's VAT-registration setting). LS + notify only — no server sync
   *  and no dirty flag, so it can't create an empty quote row or trip the
   *  Close-unsaved prompt. A no-op if the rate is already correct. */
  seedVatRate(rate: number): void;
  /** Seed the retailer's default intro/closing notes onto an untouched quote
   *  (from settings). LS + notify only — same no-sync/no-dirty contract as
   *  seedVatRate. Only fills blanks, so it never clobbers typed-in copy. */
  seedNotes(intro: string, closing: string): void;
  /** Load a specific quote from the server by its serverId (PG uuid),
   *  replacing the current working copy. Resolves true on success, false
   *  if the row could not be fetched (caller keeps the existing draft).
   *  Powers "open quote from the customer record / quotes list". */
  loadServer(serverId: string): Promise<boolean>;
  /** True when there are local edits not yet confirmed on the server
   *  (a pending/in-flight save, or content that never reached PG — e.g. a
   *  freshly duplicated quote). Drives the Close-with-confirm prompt. */
  isDirty(): boolean;
  subscribe(fn: Listener): () => void;
  /** Tear down: cancel any pending save timer, clear listeners. */
  destroy(): void;
}

export interface CreateStoreOptions {
  /** Fires when a server sync attempt fails for a generic/transport reason.
   *  The local copy is still safe — autosave keeps writing to LS and the next
   *  mutation re-triggers the sync.
   *
   *  Throttled to once every 30s, NOT once per store lifetime as it used to be.
   *  The old once-per-lifetime latch was never reset, so every failure after the
   *  first was invisible — which is a large part of why the quote-wipe bug went
   *  unnoticed for weeks. */
  onSyncError?: () => void;
  /** Fires when the server REFUSED the save because the quote changed elsewhere
   *  (last-write-wins guard, 409 stale_write). The local edits are untouched and
   *  still in LS — the user chooses between reloading and keeping them. Fires
   *  EVERY time, never throttled: this is potential data loss, not a blip. */
  onConflict?: (info: { storedVersion?: number; message?: string; reload: () => Promise<boolean> }) => void;
  /** Fires when the server refused a save that would have emptied a populated
   *  quote without explicit intent. Indicates a bug or a badly stale copy —
   *  surfaced every time. */
  onSaveBlocked?: (info: { reason: "refusing_item_wipe" | "refusing_zone_wipe"; message?: string }) => void;
  /** When set, the bootstrap loads THIS server quote (PG uuid) instead of
   *  the LS draft — used when the QB is opened against a specific quote
   *  from the customer record / quotes list. Falls back to the normal LS
   *  bootstrap if the row can't be fetched. */
  initialServerId?: string;
}

export function createStore(opts: CreateStoreOptions = {}): QuoteStore {
  let state = readFromStorage() ?? defaultQuote();
  const listeners = new Set<Listener>();
  let lsTimer: number | null = null;
  let serverTimer: number | null = null;
  let serverInFlight = false;
  let destroyed = false;
  // Tracks whether there are local edits not yet confirmed on the server.
  // Set on every mutation; cleared after a successful server sync or a
  // clean load. A freshly-restored LS draft that already carries a
  // serverId is treated as clean until the user touches it.
  let dirty = false;
  // Last time a generic sync failure was surfaced, so we can throttle without
  // ever going permanently silent (see CreateStoreOptions.onSyncError).
  let lastSyncErrorNotifiedAt = 0;
  const SYNC_ERROR_QUIET_MS = 30_000;

  // ── One-shot "the user really did empty this quote" intent ────────────────
  //
  // The server refuses any save that would take a populated quote to zero items
  // unless the payload sets `allowItemClear`. That flag must mean "a person just
  // emptied this quote", never merely "this copy happens to be empty" — get that
  // boundary wrong and either legitimate clears break, or a stale tab regains the
  // ability to wipe a quote, which is the original bug.
  //
  // So it is derived from the TRANSITION, inside set(): populated -> empty, and
  // set() is only ever reached through a user mutation. Loads and restores go via
  // replace()/loadFromServer(), which reset it.
  //
  // Deliberately a closure variable, NOT part of QuoteBuilderState: state is
  // persisted to localStorage, and a persisted flag would survive a reload and
  // let a stale tab authorise its own wipe on the next autosave.
  let pendingItemClear = false;

  function lineCountOf(q: QuoteBuilderState): number {
    let n = 0;
    for (const z of q.zones ?? []) n += (z.items ?? []).length;
    return n;
  }

  function notify(): void {
    for (const fn of listeners) {
      try { fn(state); } catch (err) {
        console.warn("[qb-store] subscriber threw:", err);
      }
    }
  }

  function persistLS(): void {
    state.lastSavedAt = Date.now();
    writeToStorage(state);
  }

  async function syncToServer(): Promise<void> {
    if (destroyed || serverInFlight) return;
    serverInFlight = true;
    const snapshot = state;
    // Captured before the await: the flag may be set again by an edit that
    // lands while this save is in flight, and that later intent belongs to the
    // NEXT save, not this one.
    const sentItemClear = pendingItemClear;
    const extra: { baseVersion?: number; allowItemClear?: boolean } = {};
    if (typeof snapshot.serverVersion === "number") extra.baseVersion = snapshot.serverVersion;
    if (sentItemClear) extra.allowItemClear = true;
    try {
      const result = snapshot.serverId
        ? await putExisting(snapshot, extra)
        : await postNew(snapshot, extra);
      if (destroyed) return;
      if (result.ok && result.serverId) {
        // Adopt server-issued ids without nuking unrelated edits.
        if (state.serverId !== result.serverId) {
          state.serverId = result.serverId;
        }
        if (result.reference && state.reference !== result.reference) {
          state.reference = result.reference;
        }
        // Adopt the post-write row version, else the very next save would send
        // the pre-write value and be refused as stale by our own guard.
        if (typeof result.version === "number") state.serverVersion = result.version;
        state.lastServerSyncAt = Date.now();
        // The clear we asked permission for has now been persisted. Only drop the
        // intent if no NEWER clear happened while this save was in flight.
        if (sentItemClear && pendingItemClear === sentItemClear) pendingItemClear = false;
        // The snapshot we sent is now safely on the server. Only clear the
        // dirty flag if no newer mutation came in while the save was in
        // flight (a later edit re-sets `dirty` via set()).
        if (snapshot === state) dirty = false;
        persistLS();
        notify();
      } else if (result.blocked === "stale_write") {
        // Someone else changed this quote since we loaded it. The write was
        // refused, so the server copy is intact, and the local edits are still
        // here and still in LS. The user decides — never silently discard
        // either side. NOT throttled: this is potential data loss.
        console.warn(
          `[qb-store] save refused: quote changed elsewhere `
          + `(ours v${snapshot.serverVersion ?? "?"}, server v${result.storedVersion ?? "?"})`,
        );
        try {
          opts.onConflict?.({
            ...(typeof result.storedVersion === "number" ? { storedVersion: result.storedVersion } : {}),
            ...(result.message ? { message: result.message } : {}),
            reload: () => loadFromServer(snapshot.serverId as string),
          });
        } catch (e) {
          console.warn("[qb-store] onConflict handler threw:", e);
        }
      } else if (result.blocked === "refusing_item_wipe" || result.blocked === "refusing_zone_wipe") {
        // The payload would have emptied a populated quote without intent —
        // i.e. exactly the wipe that cost KOS-Q-VQCX its lines twice. Reaching
        // here means a genuinely stale copy or a bug, so it is always surfaced.
        console.error(`[qb-store] save refused by the wipe guard: ${result.blocked}`);
        try {
          opts.onSaveBlocked?.({
            reason: result.blocked,
            ...(result.message ? { message: result.message } : {}),
          });
        } catch (e) {
          console.warn("[qb-store] onSaveBlocked handler threw:", e);
        }
      } else if (!result.ok) {
        // Generic failure (PUT-row-vanished, transport). The local copy is safe
        // and the next mutation re-triggers the sync. Throttled rather than
        // latched-once-forever, so repeated failures stay visible without
        // becoming a toast storm while offline.
        const now = Date.now();
        if (now - lastSyncErrorNotifiedAt > SYNC_ERROR_QUIET_MS) {
          lastSyncErrorNotifiedAt = now;
          try { opts.onSyncError?.(); } catch (e) {
            console.warn("[qb-store] onSyncError handler threw:", e);
          }
        }
      }
    } finally {
      serverInFlight = false;
    }
  }

  function scheduleLS(): void {
    if (lsTimer !== null) window.clearTimeout(lsTimer);
    lsTimer = window.setTimeout(() => {
      lsTimer = null;
      persistLS();
      notify();
    }, 80);
  }

  function scheduleServer(): void {
    if (serverTimer !== null) window.clearTimeout(serverTimer);
    serverTimer = window.setTimeout(() => {
      serverTimer = null;
      void syncToServer();
    }, 800);
  }

  function scheduleSave(): void {
    scheduleLS();
    scheduleServer();
  }

  // Load a specific server quote and adopt it as the working copy. Keeps
  // the server's own client id when present (so a later device sees the
  // same local id), else preserves the current one. Marks the result
  // clean — it mirrors the server exactly until the user edits.
  async function loadFromServer(serverId: string): Promise<boolean> {
    const fresh = await fetchById(serverId);
    if (!fresh || destroyed) return false;
    state = fromWire(fresh, fresh.clientId || state.id);
    dirty = false;
    // A fresh server copy invalidates any pending clear intent: whatever the
    // user emptied belonged to the copy we just replaced. Leaving it set would
    // let the next save wipe the newly-loaded lines.
    pendingItemClear = false;
    persistLS();
    notify();
    return true;
  }

  // ── Bootstrap: hydrate from server if we can ────────────────────────────
  //
  // 0. If opened against a specific quote (initialServerId), load THAT row
  //    — covers "open this quote from the customer record / quotes list".
  // 1. If LS has a quote with a `serverId`, refresh it from the server
  //    (server is authoritative).
  // 2. Else look it up by clientId — covers "I started a quote on my
  //    laptop, opened it on iPad".
  // 3. Else push the LS-only quote up (one-time migration).

  // A server-issued reference is KOS-Q-<retailerSig>-<random>; the client's
  // provisional one is the older single-segment KOS-Q-XXXX.
  const SERVER_REF_RE = /^KOS-Q-[0-9A-Z]{4}-[0-9A-Z]{4,8}$/;

  /** Ask the server for this quote's real reference before the first save, so a
   *  new quote shows its final KOS-Q-RRRR-XXXX from the start rather than a
   *  provisional one that visibly changes a second later. The server honours a
   *  reference it issued for this retailer, so the value sticks.
   *
   *  Best-effort: offline, the provisional reference stays on screen and the
   *  server assigns the real one on first save (the old behaviour). Note the
   *  endpoint only GENERATES a reference, it does not reserve a row, so two new
   *  quotes could in principle be handed the same string — the unique index and
   *  the insert's retry resolve that server-side. */
  async function adoptServerReference(): Promise<void> {
    if (state.serverId || SERVER_REF_RE.test(state.reference)) return;
    try {
      const res = await api<{ ok: boolean; reference?: string }>(
        `/portal/quote-builder/issue-reference`,
        { method: "POST" },
      );
      if (destroyed || !res.reference) return;
      // Re-check: a save or load may have landed while we were waiting.
      if (state.serverId || SERVER_REF_RE.test(state.reference)) return;
      state.reference = res.reference;
      persistLS();
      notify();
    } catch (err) {
      console.warn(
        "[qb-store] could not pre-fetch a reference; the server will assign one on first save",
        err,
      );
    }
  }

  void (async (): Promise<void> => {
    try {
      if (opts.initialServerId) {
        // Explicit open-by-id wins over whatever draft is in LS.
        if (await loadFromServer(opts.initialServerId)) return;
        // Row not found / fetch failed — fall through to the normal
        // bootstrap so the user still lands on a usable quote.
      }
      if (state.serverId) {
        const fresh = await fetchById(state.serverId);
        if (fresh && !destroyed) {
          state = fromWire(fresh, state.id);
          persistLS();
          notify();
          return;
        }
      }
      const byClient = await fetchByClientId(state.id);
      if (byClient && !destroyed) {
        state = fromWire(byClient, state.id);
        persistLS();
        notify();
        return;
      }
      // No server row yet — push if there's anything worth pushing
      // (avoid creating empty quote rows the moment the page mounts).
      const hasContent =
        state.customer.trim().length > 0 ||
        state.project.trim().length > 0 ||
        state.zones.some((z) => z.items.length > 0);
      // Brand-new quote with no server row: get the real reference on screen now.
      await adoptServerReference();
      if (hasContent) {
        scheduleServer();
      }
    } catch (err) {
      console.warn("[qb-store] bootstrap failed (working offline):", err);
    }
  })();

  return {
    get() { return state; },
    set(mutator) {
      const before = lineCountOf(state);
      mutator(state);
      const after = lineCountOf(state);
      // populated -> empty, via a user mutation: that is a real clear, so the
      // next save is allowed to remove the last lines. Sticky until a save
      // succeeds, because the autosave that carries it is debounced and further
      // edits may land first. Never set when `before` was already 0, so an
      // already-empty stale copy can never acquire the right to wipe.
      if (before > 0 && after === 0) pendingItemClear = true;
      dirty = true;
      scheduleSave();
    },
    loadServer(serverId) {
      return loadFromServer(serverId);
    },
    seedVatRate(rate) {
      if (state.vatRate === rate) return;
      state.vatRate = rate;
      persistLS();
      notify();
    },
    seedNotes(intro, closing) {
      let changed = false;
      if (!(state.introNote ?? "").trim() && intro) { state.introNote = intro; changed = true; }
      if (!(state.closingNote ?? "").trim() && closing) { state.closingNote = closing; changed = true; }
      if (!changed) return;
      persistLS();
      notify();
    },
    isDirty() {
      // Pending or in-flight save, or content not yet confirmed on PG.
      return dirty || serverTimer !== null || serverInFlight;
    },
    replace(next) {
      state = ensureShape(next);
      // A wholesale replace (Clear / Duplicate / Load) is not a user emptying
      // the quote they are looking at — drop any pending intent so it cannot
      // leak across quotes.
      pendingItemClear = false;
      // A replace that carries no serverId (Duplicate / Clear) produces a
      // working copy that has never been persisted server-side — treat it
      // as dirty so Close warns before discarding. A load (loadServer)
      // uses its own clean path and never routes through here.
      dirty = !(next as { serverId?: string }).serverId;
      // Preserve an explicitly-provided serverId. Loading a SAVED quote passes
      // its serverId so subsequent edits UPDATE that DB row — previously this
      // was always dropped, so the next save re-POSTed the same reference, hit
      // the unique-reference index, and every item-bearing save silently failed
      // (PG stuck as a 0-item shell). Clear (defaultQuote) and Duplicate (which
      // deletes serverId) pass no serverId, so those still start a fresh row.
      if (!(next as { serverId?: string }).serverId) {
        delete state.serverId;
      }
      delete state.lastServerSyncAt;
      persistLS();
      notify();
      scheduleServer();
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    resetLocal() {
      // Cancel the pending localStorage write FIRST — otherwise it fires after
      // the removeItem below and writes the quote straight back.
      if (lsTimer !== null) {
        window.clearTimeout(lsTimer);
        lsTimer = null;
      }
      // Cancel the pending server save too. Anything already IN FLIGHT is left
      // alone: that is a save of a real quote and must be allowed to land.
      if (serverTimer !== null) {
        window.clearTimeout(serverTimer);
        serverTimer = null;
      }
      state = ensureShape(defaultQuote());
      pendingItemClear = false;
      dirty = false;
      try {
        localStorage.removeItem(STORE_KEY);
      } catch {
        /* private mode / quota — nothing to clean up */
      }
      notify();
    },
    destroy() {
      destroyed = true;
      if (lsTimer !== null) {
        window.clearTimeout(lsTimer);
        lsTimer = null;
      }
      if (serverTimer !== null) {
        window.clearTimeout(serverTimer);
        serverTimer = null;
      }
      listeners.clear();
    },
  };
}
